import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Immutable, { type Collection } from 'immutable';
import classNames from 'classnames';
import { VideoData } from '@lumen5/luminary';

import { ApiService, Urls } from 'js/src/client';
import { BUTTON_COLORS, SPINNER_ICON } from 'js/src/components/common/ui/Button';
import type { DropdownItemProp } from 'js/src/components/common/ui/Dropdown';
import { Dropdown } from 'js/src/components/common/ui/Dropdown';
import type { ShareVideoNotification } from 'js/src/data/share-video';
import { getSupportedAspectRatios } from 'js/src/data/templates/templates-selectors';
import type { ImmutableMap } from 'js/src/libs/consts';
import { ImmutableFromJS } from 'js/src/libs/consts';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import Navigation from 'js/src/libs/navigation';
import type { RootState } from 'js/src/libs/RootState';
import { useLuminarySelector } from 'js/src/luminary/luminary-hooks';

type Props = {
    videoAspectRatio: string;
    videoId: number;
    notifyUser: (data: ShareVideoNotification) => void;
    className?: string;
};

const AutoResizeOptions = React.memo(function AutoResizeOptions({
    videoAspectRatio,
    videoId,
    notifyUser,
    className,
}: Props) {
    const [toggleProps, setToggleProps] = useState<Collection<unknown, unknown> | null>(null);
    const [itemPropsList, setItemPropsList] = useState<Immutable.List<
        DropdownItemProp<{ aspectRatio: string }>
    > | null>(null);
    const [currentlyResizing, setCurrentlyResizing] = useState(Immutable.Map<string, boolean>());

    const templateId = useLuminarySelector((luminary) => luminary.template.id, []);
    const supportedAspectRatios = useSelector((state: RootState) => {
        return getSupportedAspectRatios(state, templateId);
    });

    useEffect(() => {
        const initialResizingState = supportedAspectRatios.reduce(
            (acc, aspectRatio) => {
                acc[aspectRatio] = false;
                return acc;
            },
            {} as Record<string, boolean>,
        );
        setCurrentlyResizing(Immutable.Map(initialResizingState));
    }, [supportedAspectRatios]);

    useEffect(() => {
        const dynamicOptions = supportedAspectRatios.map((aspectRatio) => {
            return {
                text: `${VideoData.getAspectRatioDisplayName(aspectRatio)} (${VideoData.getAspectRatioDisplayRatio(aspectRatio)})`,
                icon: currentlyResizing.get(aspectRatio) ? SPINNER_ICON : undefined,
                id: aspectRatio,
                disabled: currentlyResizing.get(aspectRatio),
                onSelectData: { aspectRatio },
            };
        });

        const allOptions = ImmutableFromJS(dynamicOptions) as Immutable.List<
            DropdownItemProp<{ aspectRatio: string }>
        >;
        const anyResizingHappening = allOptions.some((option) => {
            return option.get('disabled') === true;
        });
        setItemPropsList(
            allOptions.filter((option) => {
                return option.get('id') !== videoAspectRatio;
            }),
        );
        setToggleProps(
            Immutable.fromJS({
                text: 'Reformat',
                color: BUTTON_COLORS.TERTIARY,
                iconLeft: anyResizingHappening ? SPINNER_ICON : 'aspect_ratio',
                className: 'btn-tertiary btn-sm',
            }),
        );
    }, [currentlyResizing, videoAspectRatio, supportedAspectRatios]);

    const onSelect = React.useCallback(
        (onSelectData: ImmutableMap<{ aspectRatio: string }>) => {
            const aspectRatio = onSelectData.get('aspectRatio');
            if (currentlyResizing.get(aspectRatio)) {
                return;
            }
            setCurrentlyResizing(currentlyResizing.set(aspectRatio, true));
            ApiService.videosDuplicateVideosCreate({
                body: {
                    video_ids: [videoId],
                    with_prefix: true, // adds "Copy of " to the video name
                },
            })
                .then((response) => {
                    const queryParams = new URLSearchParams({
                        target_aspect_ratio: aspectRatio,
                        id: response.data[0].id.toString(),
                    });
                    Navigation.goToLocation(Urls.APP + '?' + queryParams.toString());
                })
                .catch((e: unknown) => {
                    ErrorReporter.capture(e);
                    setCurrentlyResizing(currentlyResizing.set(aspectRatio, false));
                    notifyUser({
                        msg: 'Oops, something went wrong. Please try again.',
                        type: 'error',
                        source: 'resize',
                    });
                });

            analytics.track('Auto Resize Clicked', {
                aspect_ratio: aspectRatio,
                triggered_from: 'Share Page',
            });
            analytics.trackHSEvent('Auto Resize Clicked', {
                lumen5_auto_resize_click_date: new Date().getTime(),
            });
        },
        [currentlyResizing, notifyUser, videoId],
    );

    if (toggleProps === null || itemPropsList === null) {
        return null;
    }
    return (
        <div className={classNames('autoResize', className)} data-testid="autoResize">
            <Dropdown toggleProps={toggleProps} itemPropsList={itemPropsList} onSelect={onSelect} />
        </div>
    );
});

export default AutoResizeOptions;
